import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { authFetch } from '../../lib/auth-fetch';
import { API_BASE } from '../../lib/api-base';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Headphones, Search, Download, Filter, X, Clock, Euro, Send, RotateCcw, Lock,
  CheckCircle2, Circle, AlertCircle, User as UserIcon, Sparkles, Plus, Trash2, Settings as SettingsIcon,} from 'lucide-react';
import { localYMD } from '../../lib/dates';

const API = API_BASE;

// ─── Ampel color helper ────────────────────────────────────────────────
const STATUS_META = {
  new:               { label: 'Neu',                 dot: 'bg-red-500',    row: 'border-l-red-500',   text: 'text-red-600',    bg: 'bg-red-50 border-red-200' },
  open:              { label: 'Offen',               dot: 'bg-red-500',    row: 'border-l-red-500',   text: 'text-red-600',    bg: 'bg-red-50 border-red-200' },
  in_progress:       { label: 'In Bearbeitung',      dot: 'bg-amber-500',  row: 'border-l-amber-500', text: 'text-amber-600',  bg: 'bg-amber-50 border-amber-200' },
  waiting_customer:  { label: 'Wartet auf Kunde',    dot: 'bg-amber-400',  row: 'border-l-amber-400', text: 'text-amber-500',  bg: 'bg-amber-50 border-amber-200' },
  closed:            { label: 'Geschlossen',         dot: 'bg-green-500',  row: 'border-l-green-500', text: 'text-green-600',  bg: 'bg-green-50 border-green-200' },
};

const PRESETS = [
  { value: '',              label: 'Alle' },
  { value: 'this_week',     label: 'Diese Woche' },
  { value: 'this_month',    label: 'Aktueller Monat' },
  { value: 'last_month',    label: 'Letzter Monat' },
  { value: 'last_6_months', label: 'Letztes halbes Jahr' },
  { value: 'last_year',     label: 'Letztes Jahr' },
  { value: 'custom',        label: 'Eigener Zeitraum' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
}

function fmtEuro(n) {
  return (Number(n || 0)).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function totals(ticket, hourlyRate) {
  const minutes = (ticket.timeLogs || []).reduce((s, e) => s + (e.minutes || 0), 0);
  const hours = minutes / 60;
  const personnel = hours * (hourlyRate || 0);
  const byType = (t) => (ticket.costEntries || []).filter(c => c.type === t).reduce((s, c) => s + (c.amount || 0), 0);
  const material = byType('material'), transport = byType('transport'), other = byType('other');
  const total = personnel + material + transport + other;
  return { minutes, hours, personnel, material, transport, other, total };
}

// ─── Main page ─────────────────────────────────────────────────────────
export default function TicketsPage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [familyFilter, setFamilyFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [hourlyRate, setHourlyRate] = useState(0);
  const [users, setUsers] = useState([]);
  const isAdmin = user?.role === 'admin';
  // 04.02.2026: Matrix-basiertes Gating. Wer `tickets.delete` in der
  // Aktionsmatrix hat (per Default Admin + Technik, kann aber pro Rolle
  // ein-/ausgeschaltet werden) darf Tickets bulk-löschen / im Detail
  // löschen. Vorher war das per Rolle hardcoded — die Matrix wurde
  // ignoriert.
  const canManageTicketsLikeAdmin = can('tickets.delete');
  const [bulkIds, setBulkIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleBulk = (id) => {
    setBulkIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllBulk = (checked) => {
    if (!checked) return setBulkIds(new Set());
    setBulkIds(new Set(tickets.map(t => t.id)));
  };
  const bulkDeleteTickets = async () => {
    if (bulkIds.size === 0) return;
    if (!window.confirm(`${bulkIds.size} Tickets endgültig löschen?`)) return;
    setBulkDeleting(true);
    const res = await authFetch('/api/tickets/bulk-delete', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: Array.from(bulkIds) }),
    });
    setBulkDeleting(false);
    if (res.ok) {
      setBulkIds(new Set());
      if (selected && bulkIds.has(selected.id)) setSelected(null);
      loadTickets();
    }
  };

  const loadTickets = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (assigneeFilter) params.set('assignedTo', assigneeFilter);
    if (familyFilter) params.set('productFamily', familyFilter);
    if (search) params.set('q', search);
    const res = await authFetch(`/api/tickets${params.toString() ? '?' + params.toString() : ''}`);
    if (res.ok) setTickets(await res.json());
    setLoading(false);
  }, [statusFilter, assigneeFilter, familyFilter, search]);

  const loadSupportSettings = useCallback(async () => {
    const res = await authFetch('/api/tickets/_/settings');
    if (res.ok) { const d = await res.json(); setHourlyRate(d.hourlyRate || 0); }
  }, []);

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    const res = await authFetch('/api/auth/users');
    if (res.ok) setUsers(await res.json());
  }, [isAdmin]);

  useEffect(() => { loadTickets(); }, [loadTickets]);
  useEffect(() => { loadSupportSettings(); loadUsers(); }, [loadSupportSettings, loadUsers]);

  const refreshTicket = async (id) => {
    const res = await authFetch(`/api/tickets/${id}`);
    if (res.ok) {
      const t = await res.json();
      setSelected(t);
      setTickets(prev => prev.map(x => x.id === id ? t : x));
    }
  };

  return (
    <div className="flex h-full">
      {/* Left: ticket list — mobile: verbergen sobald Ticket ausgewählt (04.02.2026 responsive Fix). */}
      <div className={`${selected ? 'hidden lg:flex' : 'flex'} w-full lg:w-[360px] border-r border-gray-200 bg-white flex-col shrink-0`}>
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-lg font-bold text-[#32373c] flex items-center gap-2">
              <Headphones className="w-5 h-5 text-[#9ec043]" /> Support
            </h1>
            <div className="flex gap-1">
              <button onClick={() => setShowExport(true)} title="Export XLSX"
                      className="p-1.5 text-gray-400 hover:text-[#9ec043] rounded" data-testid="btn-open-export">
                <Download className="w-4 h-4" />
              </button>
              {isAdmin && (
                <button onClick={() => setShowSettings(true)} title="Support-Einstellungen"
                        className="p-1.5 text-gray-400 hover:text-[#9ec043] rounded" data-testid="btn-open-support-settings">
                  <SettingsIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
                   placeholder="Suche: Titel, Kunde, System…"
                   className="pl-8 h-9 bg-gray-50 border-gray-200 text-sm" data-testid="ticket-search" />
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            <FilterChip label="Alle" active={!statusFilter} onClick={() => setStatusFilter('')} />
            <FilterChip label="Offen" active={statusFilter === 'new'} color="red" onClick={() => setStatusFilter('new')} />
            <FilterChip label="In Bearbeitung" active={statusFilter === 'in_progress'} color="amber" onClick={() => setStatusFilter('in_progress')} />
            <FilterChip label="Geschlossen" active={statusFilter === 'closed'} color="green" onClick={() => setStatusFilter('closed')} />
          </div>
          {(assigneeFilter || familyFilter) && (
            <div className="flex flex-wrap gap-1 mt-2 text-[10px]">
              {assigneeFilter && <Chip onClear={() => setAssigneeFilter('')}>Mitarb.: {assigneeFilter}</Chip>}
              {familyFilter && <Chip onClear={() => setFamilyFilter('')}>System: {familyFilter}</Chip>}
            </div>
          )}
          <AdvancedFilters users={users} assigneeFilter={assigneeFilter} setAssigneeFilter={setAssigneeFilter}
                           familyFilter={familyFilter} setFamilyFilter={setFamilyFilter} />
        </div>
        <div className="flex-1 overflow-y-auto">
          {canManageTicketsLikeAdmin && tickets.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50 text-[11px]"
                 data-testid="tickets-bulk-bar">
              <Checkbox checked={tickets.length > 0 && bulkIds.size === tickets.length}
                        onCheckedChange={v => selectAllBulk(!!v)}
                        data-testid="tickets-select-all" />
              <span className="text-gray-500">Alle · {bulkIds.size} markiert</span>
              <Button onClick={bulkDeleteTickets} disabled={bulkIds.size === 0 || bulkDeleting}
                      variant="outline"
                      className="ml-auto h-6 text-[10px] text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-40 px-2"
                      data-testid="btn-tickets-bulk-delete">
                <Trash2 className="w-3 h-3 mr-1" /> Löschen
              </Button>
            </div>
          )}
          {loading && <div className="p-6 text-center text-xs text-gray-400">Lade…</div>}
          {!loading && tickets.length === 0 && (
            <div className="p-6 text-center text-xs text-gray-400">Keine Tickets gefunden.</div>
          )}
          {tickets.map(t => (
            <div key={t.id} className={`flex items-start gap-2 ${selected?.id === t.id ? 'bg-[#9ec043]/5' : ''}`}>
              {canManageTicketsLikeAdmin && (
                <Checkbox checked={bulkIds.has(t.id)}
                          onCheckedChange={() => toggleBulk(t.id)}
                          className="mt-4 ml-3 shrink-0"
                          data-testid={`ticket-row-select-${t.id}`} />
              )}
              <div className="flex-1 min-w-0">
                <TicketRow ticket={t} selected={selected?.id === t.id}
                           hourlyRate={hourlyRate} onClick={() => setSelected(t)} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right: detail — mobile: nur sichtbar wenn ausgewählt */}
      <div className={`${selected ? 'flex' : 'hidden lg:flex'} flex-1 bg-[#f5f6f7] overflow-y-auto min-w-0 flex-col`}>
        {selected ? (
          <>
            {/* Mobile-Zurück-Button */}
            <button
              onClick={() => setSelected(null)}
              className="lg:hidden inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#9ec043] px-4 pt-3"
              data-testid="ticket-back-to-list"
            >
              ← Zurück zur Liste
            </button>
            <TicketDetail key={selected.id} ticket={selected} user={user}
                          hourlyRate={hourlyRate} users={users}
                          onRefresh={() => refreshTicket(selected.id)}
                          onList={loadTickets} />
          </>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400 text-sm">
            Wähle ein Ticket aus der Liste
          </div>
        )}
      </div>

      {showExport && <ExportDialog onClose={() => setShowExport(false)} users={users} />}
      {showSettings && <SupportSettingsDialog hourlyRate={hourlyRate} setHourlyRate={setHourlyRate} onClose={() => setShowSettings(false)} />}
    </div>
  );
}

// ─── Filter chips / side pickers ────────────────────────────────────────
function FilterChip({ label, active, color = 'gray', onClick }) {
  const palette = {
    gray: active ? 'bg-[#9ec043] text-white border-[#9ec043]' : 'bg-white text-gray-500 border-gray-200 hover:border-[#9ec043]',
    red: active ? 'bg-red-500 text-white border-red-500' : 'bg-white text-red-500 border-red-200 hover:border-red-500',
    amber: active ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-600 border-amber-200 hover:border-amber-500',
    green: active ? 'bg-green-500 text-white border-green-500' : 'bg-white text-green-600 border-green-200 hover:border-green-500',
  };
  return (
    <button onClick={onClick} className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${palette[color]}`} data-testid={`chip-status-${label.replace(/\s/g,'-')}`}>
      {label}
    </button>
  );
}

function Chip({ children, onClear }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#9ec043]/10 text-[#9ec043] rounded-full">
      {children}
      <button onClick={onClear} className="hover:text-red-500"><X className="w-3 h-3" /></button>
    </span>
  );
}

function AdvancedFilters({ users, assigneeFilter, setAssigneeFilter, familyFilter, setFamilyFilter }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button onClick={() => setOpen(o => !o)} className="text-[10px] text-gray-400 hover:text-[#9ec043] flex items-center gap-1" data-testid="btn-advanced-filters">
        <Filter className="w-3 h-3" /> Erweiterte Filter
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          <div>
            <Label className="text-[10px] text-gray-400">Zugewiesen an</Label>
            <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}
                    className="w-full h-8 rounded-md border border-gray-200 bg-white px-2 text-xs" data-testid="filter-assignee">
              <option value="">Alle</option>
              {users.map(u => <option key={u.email} value={u.email}>{u.name} ({u.email})</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] text-gray-400">Produktfamilie</Label>
            <select value={familyFilter} onChange={e => setFamilyFilter(e.target.value)}
                    className="w-full h-8 rounded-md border border-gray-200 bg-white px-2 text-xs" data-testid="filter-family">
              <option value="">Alle</option>
              {['HS', 'HS PV', 'HS PV duo', 'HS GenLink', 'GS', 'LS'].map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ticket Row ────────────────────────────────────────────────────────
function TicketRow({ ticket, selected, onClick, hourlyRate }) {
  const meta = STATUS_META[ticket.status] || STATUS_META.new;
  const t = totals(ticket, hourlyRate);
  return (
    <button onClick={onClick}
            className={`w-full text-left p-3 border-b border-gray-100 border-l-4 ${meta.row} ${selected ? 'bg-[#9ec043]/5' : 'hover:bg-gray-50'} transition-colors`}
            data-testid={`ticket-row-${ticket.id}`}>
      <div className="flex items-start gap-2">
        <span className={`${meta.dot} w-2 h-2 rounded-full mt-1.5 shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-[#32373c] truncate">{ticket.title}</div>
          <div className="text-[10px] text-gray-400 truncate mt-0.5">
            {ticket.customerName || '—'}{ticket.customerEmail ? ` · ${ticket.customerEmail}` : ''}
          </div>
          <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-400">
            <span className={meta.text}>{meta.label}</span>
            <span>·</span>
            <span>{fmtDate(ticket.createdAt)}</span>
            {t.hours > 0 && (<><span>·</span><span><Clock className="w-2.5 h-2.5 inline" /> {t.hours.toFixed(1)}h</span></>)}
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── Ticket Detail ─────────────────────────────────────────────────────
function TicketDetail({ ticket, user, hourlyRate, users, onRefresh, onList }) {
  const { can } = usePermissions();
  const [tab, setTab] = useState('overview');
  const [aiSummary, setAiSummary] = useState(ticket.aiSummary?.text || '');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const meta = STATUS_META[ticket.status] || STATUS_META.new;
  const t = totals(ticket, hourlyRate);
  const isAssignee = ticket.assignedTo && ticket.assignedTo === user?.email;
  const isAdmin = user?.role === 'admin';
  // 04.02.2026: Matrix-basiertes Gating (analog zur Liste oben).
  // `tickets.write` erlaubt Schließen, `tickets.delete` erlaubt Löschen.
  const canManageTicketsLikeAdmin = can('tickets.delete');
  const canClose = isAssignee || can('tickets.write');

  const generateSummary = async () => {
    setLoadingSummary(true);
    const res = await authFetch(`/api/tickets/${ticket.id}/summary`, { method: 'POST' });
    if (res.ok) { const d = await res.json(); setAiSummary(d.text); }
    setLoadingSummary(false);
  };

  const assignTo = async (email) => {
    await authFetch(`/api/tickets/${ticket.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assignedTo: email || null, status: email ? 'in_progress' : ticket.status }),
    });
    onRefresh(); onList();
  };

  const setStatus = async (status) => {
    await authFetch(`/api/tickets/${ticket.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    onRefresh(); onList();
  };

  const closeTicket = async () => {
    const note = window.prompt('Abschluss-Notiz (optional):', '') || '';
    const res = await authFetch(`/api/tickets/${ticket.id}/close`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resolutionNote: note }),
    });
    if (res.ok) { onRefresh(); onList(); }
    else { const d = await res.json().catch(()=>({})); alert(d.detail || 'Schließen fehlgeschlagen'); }
  };

  const reopenTicket = async () => {
    await authFetch(`/api/tickets/${ticket.id}/reopen`, { method: 'POST' });
    onRefresh(); onList();
  };

  const deleteTicket = async () => {
    if (!canManageTicketsLikeAdmin) return;
    if (!window.confirm(`Ticket #${ticket.id.slice(0,8)} „${ticket.title}" unwiderruflich löschen?\n\nVerknüpfte Aufgaben werden ebenfalls entfernt.`)) return;
    const res = await authFetch(`/api/tickets/${ticket.id}`, { method: 'DELETE' });
    if (res.ok) { onRefresh(); onList(); }
    else { const d = await res.json().catch(()=>({})); alert(d.detail || 'Löschen fehlgeschlagen'); }
  };

  return (
    <div className="p-6 max-w-5xl" data-testid="ticket-detail">
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <span className={`${meta.dot} w-3 h-3 rounded-full mt-1.5 shrink-0`} />
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-[#32373c]" data-testid="ticket-title">{ticket.title}</h1>
          <div className="text-xs text-gray-400 mt-0.5">
            #{ticket.id.slice(0, 8)} · {fmtDate(ticket.createdAt)}
            {ticket.closedAt && <> · geschlossen {fmtDate(ticket.closedAt)} von {ticket.closedBy}</>}
          </div>
        </div>
        <span className={`text-[11px] px-3 py-1 rounded-full border ${meta.bg} ${meta.text}`}>{meta.label}</span>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select value={ticket.assignedTo || ''} onChange={e => assignTo(e.target.value)}
                className="h-8 rounded-md border border-gray-200 bg-white px-2 text-xs" data-testid="ticket-assign-select">
          <option value="">Nicht zugewiesen</option>
          {users.map(u => <option key={u.email} value={u.email}>{u.name} ({u.email})</option>)}
        </select>
        {ticket.status !== 'closed' && (
          <Button size="sm" variant="outline"
                  onClick={async () => {
                    const title = window.prompt('Titel der Aufgabe:', `Support-Fall #${ticket.id.slice(0,8)}: ${ticket.title}`);
                    if (!title) return;
                    const res = await authFetch('/api/tasks', {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        title, description: ticket.description || '',
                        priority: ticket.priority === 'high' ? 'high' : 'medium',
                        source: 'ticket', sourceId: ticket.id,
                        sourceLabel: `Ticket #${ticket.id.slice(0,8)} · ${ticket.customerName || ticket.customerEmail || ''}`,
                        assignees: ticket.assignedTo ? [ticket.assignedTo] : [],
                      }),
                    });
                    if (res.ok) alert('Aufgabe angelegt – im Tab "Aufgaben" sichtbar.');
                  }}
                  className="h-8 text-xs" data-testid="btn-ticket-to-task">
            <Plus className="w-3 h-3 mr-1" /> Aufgabe erstellen
          </Button>
        )}
        {ticket.status !== 'closed' && ticket.status !== 'in_progress' && (
          <Button size="sm" variant="outline" onClick={() => setStatus('in_progress')} className="h-8 text-xs" data-testid="btn-status-progress">
            In Bearbeitung setzen
          </Button>
        )}
        {ticket.status !== 'closed' && (
          <Button size="sm" disabled={!canClose}
                  onClick={closeTicket}
                  className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white disabled:opacity-40"
                  data-testid="btn-close-ticket"
                  title={canClose ? 'Ticket endgültig schließen' : 'Nur zugewiesener Mitarbeiter, Techniker oder Admin'}>
            <Lock className="w-3 h-3 mr-1" /> Ticket schließen
          </Button>
        )}
        {ticket.status === 'closed' && (
          <Button size="sm" variant="outline" onClick={reopenTicket}
                  className="h-8 text-xs" data-testid="btn-reopen-ticket">
            <RotateCcw className="w-3 h-3 mr-1" /> Wieder öffnen
          </Button>
        )}
        {canManageTicketsLikeAdmin && (
          <Button size="sm" variant="outline" onClick={deleteTicket}
                  className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50 ml-auto"
                  data-testid="btn-delete-ticket">
            <Trash2 className="w-3 h-3 mr-1" /> Löschen
          </Button>
        )}
      </div>

      {/* AI Briefing */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#9ec043]" /> KI-Briefing
          </h3>
          <Button variant="outline" size="sm" onClick={generateSummary} disabled={loadingSummary}
                  className="h-7 text-[11px]" data-testid="btn-generate-summary">
            {loadingSummary ? 'Generiere…' : (aiSummary ? 'Neu generieren' : 'Generieren')}
          </Button>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
          {aiSummary || 'Noch kein Briefing erstellt. Klicke "Generieren" für eine kompakte Technik-Zusammenfassung.'}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-4 overflow-x-auto">
        {[
          { id: 'overview', label: 'Übersicht' },
          { id: 'rca', label: 'RCA / Fehleranalyse' },
          { id: 'time', label: 'Zeit & Kosten' },
          { id: 'reply', label: 'E-Mail' },
          { id: 'chat', label: 'Chat-Verlauf' },
        ].map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)}
                  className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap ${tab === tb.id ? 'border-[#9ec043] text-[#9ec043]' : 'border-transparent text-gray-400'}`}
                  data-testid={`ticket-tab-${tb.id}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab ticket={ticket} totals={t} hourlyRate={hourlyRate} />}
      {tab === 'rca' && <RcaTab ticket={ticket} onRefresh={onRefresh} />}
      {tab === 'time' && <TimeCostTab ticket={ticket} totals={t} hourlyRate={hourlyRate} onRefresh={onRefresh} />}
      {tab === 'reply' && <ReplyTab ticket={ticket} onRefresh={onRefresh} />}
      {tab === 'chat' && <ChatTab ticket={ticket} />}
    </div>
  );
}

function OverviewTab({ ticket, totals: t, hourlyRate }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <InfoCard label="Kunde" value={ticket.customerName || '—'}
                  sub={[ticket.customerEmail, ticket.customerPhone, ticket.postalCode].filter(Boolean).join(' · ')} />
        <InfoCard label="System / Produkt" value={ticket.systemLabel || ticket.productFamily || '—'}
                  sub={ticket.knowledgeEntryTitle ? `KB: ${ticket.knowledgeEntryTitle}` : ''} />
        <InfoCard label="Gesamtkosten" value={fmtEuro(t.total)}
                  sub={`${t.hours.toFixed(2)} h · Stundensatz ${fmtEuro(hourlyRate)}`} />
      </div>
      {ticket.description && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-xs font-semibold text-[#32373c] mb-1">Beschreibung</h3>
          <p className="text-sm text-gray-600 whitespace-pre-wrap">{ticket.description}</p>
        </div>
      )}
      {ticket.internalNotes?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-xs font-semibold text-[#32373c] mb-2">Interne Notizen</h3>
          <div className="space-y-2">
            {ticket.internalNotes.map((n, i) => (
              <div key={i} className="text-xs border-l-2 border-[#9ec043]/40 pl-2">
                <div className="text-gray-400">{n.author} · {fmtDate(n.time)}</div>
                <div className="text-gray-600 whitespace-pre-wrap">{n.text}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InfoCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-3">
      <div className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</div>
      <div className="text-sm font-medium text-[#32373c] mt-0.5 truncate">{value}</div>
      {sub && <div className="text-[11px] text-gray-400 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

// ─── RCA (Root Cause Analysis) Tab ─────────────────────────────────────
function RcaTab({ ticket, onRefresh }) {
  const rca = ticket.rca || {};
  const [busy, setBusy] = useState(null); // 'analyze' | 'recommend' | 'summarize' | 'approve' | 'save-diag' | 'save-resol'
  const [err, setErr] = useState(null);
  const [okMsg, setOkMsg] = useState(null);
  const [empDiag, setEmpDiag] = useState(rca.employeeDiagnosis?.text || '');
  const [empResol, setEmpResol] = useState(rca.employeeResolution?.text || '');

  useEffect(() => {
    setEmpDiag(ticket.rca?.employeeDiagnosis?.text || '');
    setEmpResol(ticket.rca?.employeeResolution?.text || '');
  }, [ticket.id, ticket.rca?.employeeDiagnosis?.text, ticket.rca?.employeeResolution?.text]);

  const runAction = async (action, url, opts = {}) => {
    setBusy(action); setErr(null); setOkMsg(null);
    const res = await authFetch(url, { method: 'POST', ...opts });
    setBusy(null);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setErr(d.detail || 'Fehler');
      return false;
    }
    onRefresh();
    return res;
  };

  const saveDiag = async () => {
    setBusy('save-diag'); setErr(null); setOkMsg(null);
    const res = await authFetch(`/api/tickets/${ticket.id}/rca`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employeeDiagnosis: empDiag }),
    });
    setBusy(null);
    if (res.ok) { setOkMsg('Diagnose gespeichert'); onRefresh(); }
    else { const d = await res.json().catch(() => ({})); setErr(d.detail || 'Speichern fehlgeschlagen'); }
  };

  const saveResol = async () => {
    setBusy('save-resol'); setErr(null); setOkMsg(null);
    const res = await authFetch(`/api/tickets/${ticket.id}/rca`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employeeResolution: empResol }),
    });
    setBusy(null);
    if (res.ok) { setOkMsg('Fehlerbehebung gespeichert'); onRefresh(); }
    else { const d = await res.json().catch(() => ({})); setErr(d.detail || 'Speichern fehlgeschlagen'); }
  };

  const approve = async () => {
    if (!window.confirm('Diese KI-Zusammenfassung in die Wissensdatenbank übernehmen?\n\nSie wird dort für Frag HANS und Kollegen sichtbar, verknüpft mit diesem Ticket.')) return;
    setBusy('approve'); setErr(null); setOkMsg(null);
    const res = await authFetch(`/api/tickets/${ticket.id}/rca/approve-to-kb`, { method: 'POST' });
    setBusy(null);
    if (res.ok) { setOkMsg('In Wissensdatenbank übernommen — Frag HANS nutzt das ab sofort.'); onRefresh(); }
    else { const d = await res.json().catch(() => ({})); setErr(d.detail || 'Übernahme fehlgeschlagen'); }
  };

  const approved = !!rca.aiSummary?.approvedAt;

  return (
    <div className="space-y-4" data-testid="rca-tab">
      {err && <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-600" data-testid="rca-error">{err}</div>}
      {okMsg && <div className="bg-green-50 border border-green-200 rounded p-3 text-xs text-green-700" data-testid="rca-ok">{okMsg}</div>}

      {/* 1. KI-Fehleranalyse */}
      <RcaCard num="1" color="amber"
               title="KI-Fehleranalyse"
               subtitle="Symptom + Hypothesen — KI-generiert aus Ticket + Chat"
               data={rca.aiAnalysis}
               action={<Button size="sm" variant="outline" className="h-7 text-[11px]"
                         onClick={() => runAction('analyze', `/api/tickets/${ticket.id}/rca/analyze`)}
                         disabled={busy === 'analyze'} data-testid="btn-rca-analyze">
                 <Sparkles className="w-3 h-3 mr-1" />
                 {busy === 'analyze' ? 'Analysiere…' : (rca.aiAnalysis ? 'Neu analysieren' : 'Analyse starten')}
               </Button>}
               testid="rca-ai-analysis" />

      {/* 2. Mitarbeiter-Diagnose */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 mr-2">2</span>
            <span className="text-sm font-semibold text-[#32373c]">Mitarbeiter-Diagnose</span>
            <span className="text-[11px] text-gray-400 ml-2">Dein Urteil nach der Analyse</span>
          </div>
          <Button size="sm" onClick={saveDiag} disabled={busy === 'save-diag'}
                  className="h-7 text-[11px] bg-[#9ec043] hover:bg-[#8aad38] text-white" data-testid="btn-save-diag">
            {busy === 'save-diag' ? 'Speichert…' : 'Speichern'}
          </Button>
        </div>
        <textarea value={empDiag} onChange={e => setEmpDiag(e.target.value)} rows={4}
                  placeholder="z. B.: Kunde berichtet von sporadischen Abschaltungen bei hoher Solar-Einstrahlung. Nach Telefonat wahrscheinlich Übertemperatur am DC/DC-Wandler."
                  className="w-full text-sm rounded-md border border-gray-200 bg-white px-3 py-2 leading-relaxed focus:outline-none focus:border-[#9ec043]"
                  data-testid="input-emp-diag" />
        {rca.employeeDiagnosis?.updatedAt && (
          <div className="text-[10px] text-gray-400 mt-1">
            Zuletzt aktualisiert von {rca.employeeDiagnosis.author} · {fmtDate(rca.employeeDiagnosis.updatedAt)}
          </div>
        )}
      </div>

      {/* 3. KI-Behebungsempfehlung */}
      <RcaCard num="3" color="amber"
               title="KI-Behebungsempfehlung"
               subtitle="Aus Wissensdatenbank + internen Dokumenten"
               data={rca.aiRecommendation}
               action={<Button size="sm" variant="outline" className="h-7 text-[11px]"
                         onClick={() => runAction('recommend', `/api/tickets/${ticket.id}/rca/recommend`)}
                         disabled={busy === 'recommend'} data-testid="btn-rca-recommend">
                 <Sparkles className="w-3 h-3 mr-1" />
                 {busy === 'recommend' ? 'Suche Lösung…' : (rca.aiRecommendation ? 'Neu empfehlen' : 'Empfehlung generieren')}
               </Button>}
               testid="rca-ai-recommendation"
               sources={rca.aiRecommendation?.sources} />

      {/* 4. Mitarbeiter-Fehlerbehebung */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 mr-2">4</span>
            <span className="text-sm font-semibold text-[#32373c]">Mitarbeiter-Fehlerbehebung</span>
            <span className="text-[11px] text-gray-400 ml-2">Was wurde tatsächlich gemacht?</span>
          </div>
          <Button size="sm" onClick={saveResol} disabled={busy === 'save-resol'}
                  className="h-7 text-[11px] bg-[#9ec043] hover:bg-[#8aad38] text-white" data-testid="btn-save-resol">
            {busy === 'save-resol' ? 'Speichert…' : 'Speichern'}
          </Button>
        </div>
        <textarea value={empResol} onChange={e => setEmpResol(e.target.value)} rows={5}
                  placeholder="z. B.: Vor-Ort-Termin 12.04.: DC/DC-Kühlkörper verstaubt, gereinigt; Firmware 3.4.1 → 3.4.3 aktualisiert; 48 h Last-Test OK. Kunde informiert."
                  className="w-full text-sm rounded-md border border-gray-200 bg-white px-3 py-2 leading-relaxed focus:outline-none focus:border-[#9ec043]"
                  data-testid="input-emp-resol" />
        {rca.employeeResolution?.updatedAt && (
          <div className="text-[10px] text-gray-400 mt-1">
            Zuletzt aktualisiert von {rca.employeeResolution.author} · {fmtDate(rca.employeeResolution.updatedAt)}
          </div>
        )}
      </div>

      {/* 5. KI-Abschluss-Zusammenfassung + Approve */}
      <div className={`rounded-xl border p-4 ${approved ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`} data-testid="rca-ai-summary">
        <div className="flex items-center justify-between mb-2">
          <div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full mr-2 ${approved ? 'bg-green-600 text-white' : 'bg-amber-50 text-amber-700'}`}>5</span>
            <span className="text-sm font-semibold text-[#32373c]">KI-Abschluss-Zusammenfassung</span>
            <span className="text-[11px] text-gray-400 ml-2">Ursache + Behebung · Vorschlag für Wissensdatenbank</span>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => runAction('summarize', `/api/tickets/${ticket.id}/rca/summarize`)}
                    disabled={busy === 'summarize' || !(rca.employeeResolution?.text || empResol).trim()}
                    title={!(rca.employeeResolution?.text || empResol).trim() ? 'Fehlerbehebung zuerst ausfüllen' : ''}
                    data-testid="btn-rca-summarize">
              <Sparkles className="w-3 h-3 mr-1" />
              {busy === 'summarize' ? 'Fasst zusammen…' : (rca.aiSummary ? 'Neu zusammenfassen' : 'Zusammenfassen')}
            </Button>
            {rca.aiSummary && !approved && (
              <Button size="sm" onClick={approve} disabled={busy === 'approve'}
                      className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="btn-rca-approve">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                {busy === 'approve' ? 'Übernehme…' : 'In Wissensdatenbank übernehmen'}
              </Button>
            )}
            {approved && (
              <span className="text-[11px] px-2 py-1 rounded bg-green-600 text-white flex items-center gap-1" data-testid="rca-approved-badge">
                <CheckCircle2 className="w-3 h-3" /> Übernommen am {fmtDate(rca.aiSummary.approvedAt)}
              </span>
            )}
          </div>
        </div>
        {rca.aiSummary ? (
          <div className="space-y-2 mt-3">
            <div className="text-sm font-semibold text-[#32373c]">{rca.aiSummary.title}</div>
            <div className="flex gap-2 flex-wrap text-[11px]">
              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{rca.aiSummary.category}</span>
              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{rca.aiSummary.productFamily}</span>
              {rca.aiSummary.safetyLevel && (
                <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{rca.aiSummary.safetyLevel}</span>
              )}
              {(rca.aiSummary.tags || []).map((t, i) => <span key={i} className="px-2 py-0.5 rounded-full bg-[#9ec043]/10 text-[#9ec043]">{t}</span>)}
            </div>
            <div className="text-xs text-gray-700 space-y-2 mt-2">
              <div><strong className="text-gray-900">Ursache:</strong> {rca.aiSummary.cause}</div>
              <div><strong className="text-gray-900">Behebung:</strong> {rca.aiSummary.resolution}</div>
              <div className="border-t border-gray-200 pt-2 text-[11px] italic text-gray-600">
                KB-Text: {rca.aiSummary.contentForKb}
              </div>
            </div>
            {rca.aiSummary.generatedAt && (
              <div className="text-[10px] text-gray-400 mt-2">
                Generiert {fmtDate(rca.aiSummary.generatedAt)} · {rca.aiSummary.model}
                {rca.aiSummary.kbEntryId && <> · KB-Eintrag {rca.aiSummary.kbEntryId.slice(0, 8)}</>}
              </div>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">
            Sobald Diagnose + Fehlerbehebung ausgefüllt sind, kann die KI eine Lessons-Learned-Zusammenfassung erstellen.
            Nach Freigabe landet sie als Wissenseintrag in der Wissensdatenbank und Frag HANS lernt automatisch mit.
          </p>
        )}
      </div>
    </div>
  );
}

function RcaCard({ num, title, subtitle, data, action, sources, testid }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 mr-2">{num}</span>
          <span className="text-sm font-semibold text-[#32373c]">{title}</span>
          <span className="text-[11px] text-gray-400 ml-2">{subtitle}</span>
        </div>
        {action}
      </div>
      {data?.text ? (
        <>
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{data.text}</p>
          {sources && (sources.docHits?.length > 0 || sources.kbEntries?.length > 0) && (
            <div className="mt-2 flex flex-wrap gap-1">
              {(sources.docHits || []).map((h, i) => (
                <span key={`d${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                  📄 {h.title} · Seite {(h.pageIndex || 0) + 1}
                </span>
              ))}
              {(sources.kbEntries || []).map((e, i) => (
                <span key={`k${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                  📘 {e.title}
                </span>
              ))}
            </div>
          )}
          {data.generatedAt && (
            <div className="text-[10px] text-gray-400 mt-2">Generiert {fmtDate(data.generatedAt)} · {data.model}</div>
          )}
        </>
      ) : (
        <p className="text-xs text-gray-400 italic">Noch keine KI-Analyse — Button rechts oben klicken.</p>
      )}
    </div>
  );
}


function TimeCostTab({ ticket, totals: t, hourlyRate, onRefresh }) {
  const [minutes, setMinutes] = useState(30);
  const [note, setNote] = useState('');
  const [costType, setCostType] = useState('material');
  const [costAmount, setCostAmount] = useState('');
  const [costDesc, setCostDesc] = useState('');

  const addTime = async () => {
    if (!minutes || minutes <= 0) return;
    const res = await authFetch(`/api/tickets/${ticket.id}/time-log`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ minutes: Number(minutes), note }),
    });
    if (res.ok) { setMinutes(30); setNote(''); onRefresh(); }
  };
  const deleteTime = async (id) => {
    if (!window.confirm('Eintrag löschen?')) return;
    await authFetch(`/api/tickets/${ticket.id}/time-log/${id}`, { method: 'DELETE' });
    onRefresh();
  };
  const addCost = async () => {
    if (!costAmount) return;
    const res = await authFetch(`/api/tickets/${ticket.id}/cost-entry`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: costType, amount: Number(costAmount), description: costDesc }),
    });
    if (res.ok) { setCostAmount(''); setCostDesc(''); onRefresh(); }
  };
  const deleteCost = async (id) => {
    if (!window.confirm('Eintrag löschen?')) return;
    await authFetch(`/api/tickets/${ticket.id}/cost-entry/${id}`, { method: 'DELETE' });
    onRefresh();
  };

  return (
    <div className="space-y-4">
      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <InfoCard label="Zeit" value={`${t.hours.toFixed(2)} h`} sub={`${t.minutes} min`} />
        <InfoCard label="Personal" value={fmtEuro(t.personnel)} sub={`${fmtEuro(hourlyRate)}/h`} />
        <InfoCard label="Material" value={fmtEuro(t.material)} />
        <InfoCard label="Transport" value={fmtEuro(t.transport)} />
        <InfoCard label="Gesamt" value={fmtEuro(t.total)} />
      </div>

      {/* Time log */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-[#32373c] mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#9ec043]" /> Zeiterfassung
        </h3>
        <div className="flex flex-wrap gap-2 mb-3">
          <Input type="number" value={minutes} onChange={e => setMinutes(e.target.value)}
                 className="w-24 h-9 text-sm" placeholder="Min" data-testid="time-minutes" />
          <Input value={note} onChange={e => setNote(e.target.value)}
                 className="flex-1 h-9 text-sm min-w-[200px]" placeholder="Notiz (z. B. Rückruf Kunde)" data-testid="time-note" />
          <Button onClick={addTime} className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm" data-testid="btn-add-time">
            <Plus className="w-3.5 h-3.5 mr-1" /> Zeit buchen
          </Button>
        </div>
        <div className="space-y-1">
          {(ticket.timeLogs || []).length === 0 && (
            <p className="text-xs text-gray-400">Noch keine Zeiten erfasst.</p>
          )}
          {(ticket.timeLogs || []).map(e => (
            <div key={e.id} className="flex items-center gap-2 text-xs border-b border-gray-100 py-1.5">
              <span className="font-medium text-[#32373c] w-20">{e.minutes} min</span>
              <span className="text-gray-400 w-36 truncate">{fmtDate(e.date)}</span>
              <span className="flex-1 text-gray-600 truncate">{e.note || '—'}</span>
              <span className="text-gray-400 text-[10px] w-40 truncate">{e.author}</span>
              <button onClick={() => deleteTime(e.id)} className="text-gray-300 hover:text-red-500" data-testid={`btn-del-time-${e.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Cost entries */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-[#32373c] mb-3 flex items-center gap-2">
          <Euro className="w-4 h-4 text-[#9ec043]" /> Kostenpositionen
        </h3>
        <div className="flex flex-wrap gap-2 mb-3">
          <select value={costType} onChange={e => setCostType(e.target.value)}
                  className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="cost-type">
            <option value="material">Material</option>
            <option value="transport">Transport / Versand</option>
            <option value="other">Sonstiges</option>
          </select>
          <Input type="number" step="0.01" value={costAmount} onChange={e => setCostAmount(e.target.value)}
                 className="w-28 h-9 text-sm" placeholder="€" data-testid="cost-amount" />
          <Input value={costDesc} onChange={e => setCostDesc(e.target.value)}
                 className="flex-1 h-9 text-sm min-w-[200px]" placeholder="Beschreibung" data-testid="cost-desc" />
          <Button onClick={addCost} className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm" data-testid="btn-add-cost">
            <Plus className="w-3.5 h-3.5 mr-1" /> Kosten buchen
          </Button>
        </div>
        <div className="space-y-1">
          {(ticket.costEntries || []).length === 0 && (
            <p className="text-xs text-gray-400">Keine Kosten erfasst.</p>
          )}
          {(ticket.costEntries || []).map(e => (
            <div key={e.id} className="flex items-center gap-2 text-xs border-b border-gray-100 py-1.5">
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 w-20 text-center capitalize">{e.type}</span>
              <span className="font-medium text-[#32373c] w-24 text-right">{fmtEuro(e.amount)}</span>
              <span className="flex-1 text-gray-600 truncate">{e.description || '—'}</span>
              <span className="text-gray-400 text-[10px] w-40 truncate">{e.author}</span>
              <button onClick={() => deleteCost(e.id)} className="text-gray-300 hover:text-red-500" data-testid={`btn-del-cost-${e.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReplyTab({ ticket, onRefresh }) {
  const [settings, setSettings] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingId, setSettingId] = useState('');
  const [to, setTo] = useState(ticket.customerEmail || '');
  const [subject, setSubject] = useState(`Re: ${ticket.title}`);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // 01.09.2026 (Bug-Fix): Loading-State — sonst blinkt beim Laden
    // kurz „Keine Mailserver-Konfiguration vorhanden" auf, obwohl der
    // Absender direkt danach kommt.
    setSettingsLoading(true);
    authFetch('/api/mail/settings').then(r => r.ok ? r.json() : []).then(list => {
      setSettings(list);
      const def = list.find(s => s.isDefault) || list[0];
      if (def) setSettingId(def.id);
      setSettingsLoading(false);
    }).catch(() => setSettingsLoading(false));
  }, []);

  const currentSig = useMemo(() => settings.find(s => s.id === settingId)?.signatureHtml || '', [settings, settingId]);

  const send = async () => {
    setError(''); setSending(true);
    try {
      const res = await authFetch(`/api/tickets/${ticket.id}/reply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settingId, to, subject, bodyHtml: body }),
      });
      if (!res.ok) {
        const d = await res.json().catch(()=>({}));
        setError(d.detail || 'Versand fehlgeschlagen');
        return;
      }
      setBody(''); onRefresh();
    } finally { setSending(false); }
  };

  if (settingsLoading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm text-gray-500 animate-pulse">
        Absender werden geladen …
      </div>
    );
  }
  if (settings.length === 0) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
        Keine Mailserver-Konfiguration vorhanden. Bitte zuerst unter Einstellungen → Mailserver einen Absender anlegen.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-3 text-sm">{error}</div>}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Absender</Label>
            <select value={settingId} onChange={e => setSettingId(e.target.value)}
                    className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="reply-sender">
              {settings.map(s => (
                <option key={s.id} value={s.id}>
                  {s.label} ({s.senderEmail}){s.isDefault ? ' — Standard' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Empfänger</Label>
            <Input type="email" value={to} onChange={e => setTo(e.target.value)}
                   className="mt-1 h-9 text-sm" data-testid="reply-to" />
          </div>
        </div>
        <div>
          <Label className="text-xs text-gray-500">Betreff</Label>
          <Input value={subject} onChange={e => setSubject(e.target.value)}
                 className="mt-1 h-9 text-sm" data-testid="reply-subject" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Nachricht (HTML-tauglich)</Label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={10}
                    placeholder="Guten Tag …"
                    className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#9ec043]"
                    data-testid="reply-body" />
        </div>
        {currentSig && (
          <div>
            <Label className="text-xs text-gray-500">Signatur-Vorschau (wird automatisch angehängt)</Label>
            <div className="mt-1 rounded-md border border-dashed border-gray-200 bg-gray-50 p-3 text-xs text-gray-600" dangerouslySetInnerHTML={{ __html: currentSig }} />
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={send} disabled={sending || !to || !subject || !body}
                  className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm" data-testid="btn-reply-send">
            <Send className="w-3.5 h-3.5 mr-1" /> {sending ? 'Sende…' : 'Senden'}
          </Button>
        </div>
      </div>
      {ticket.replies?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-[#32373c] mb-3">Versendete E-Mails ({ticket.replies.length})</h3>
          <div className="space-y-3">
            {ticket.replies.map(r => (
              <div key={r.id} className="border border-gray-100 rounded-lg p-3">
                <div className="flex items-center gap-2 text-[11px] text-gray-400 mb-1">
                  <span>{fmtDate(r.sentAt)}</span>
                  <span>·</span>
                  <span>{r.from} → {r.to}</span>
                </div>
                <div className="text-xs font-medium text-[#32373c] mb-1">{r.subject}</div>
                <div className="text-xs text-gray-600" dangerouslySetInnerHTML={{ __html: r.bodyHtml }} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ChatTab({ ticket }) {
  const transcript = ticket.chatTranscript || [];
  if (transcript.length === 0) {
    return <div className="text-sm text-gray-400">Keine Chat-Historie.</div>;
  }
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3 max-h-[70vh] overflow-y-auto">
      {transcript.map((m, i) => (
        <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : ''}`}>
          <div className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm ${m.role === 'user' ? 'bg-[#9ec043] text-white' : 'bg-gray-100 text-[#32373c]'}`}>
            <div className="text-[10px] opacity-70 mb-0.5">{m.role === 'user' ? 'Kunde' : 'Hans'} · {fmtDate(m.time)}</div>
            <div className="whitespace-pre-wrap break-words">{m.content}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Export dialog ─────────────────────────────────────────────────────
function ExportDialog({ onClose, users }) {
  const [preset, setPreset] = useState('this_month');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState('');
  const [family, setFamily] = useState('');
  const [assignee, setAssignee] = useState('');
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    const params = new URLSearchParams();
    if (preset !== 'custom' && preset) params.set('preset', preset);
    if (preset === 'custom') {
      if (fromDate) params.set('fromDate', new Date(fromDate).toISOString());
      if (toDate) params.set('toDate', new Date(toDate + 'T23:59:59').toISOString());
    }
    if (status) params.set('status', status);
    if (family) params.set('productFamily', family);
    if (assignee) params.set('assignedTo', assignee);
    try {
      const res = await authFetch(`/api/tickets/export/xlsx?${params.toString()}`);
      if (!res.ok) { alert('Export fehlgeschlagen'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `supportfaelle_${localYMD(new Date())}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      onClose();
    } finally { setDownloading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" data-testid="export-dialog">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-5">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-semibold text-[#32373c] flex items-center gap-2">
            <Download className="w-4 h-4 text-[#9ec043]" /> Supportfälle exportieren
          </h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-gray-500">Zeitraum</Label>
            <select value={preset} onChange={e => setPreset(e.target.value)}
                    className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="export-preset">
              {PRESETS.filter(p => p.value).map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          {preset === 'custom' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">Von</Label>
                <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="mt-1 h-9 text-sm" data-testid="export-from" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Bis</Label>
                <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="mt-1 h-9 text-sm" data-testid="export-to" />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Status</Label>
              <select value={status} onChange={e => setStatus(e.target.value)}
                      className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="export-status">
                <option value="">Alle</option>
                <option value="new">Neu / Offen</option>
                <option value="in_progress">In Bearbeitung</option>
                <option value="waiting_customer">Wartet auf Kunde</option>
                <option value="closed">Geschlossen</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Produktfamilie</Label>
              <select value={family} onChange={e => setFamily(e.target.value)}
                      className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="export-family">
                <option value="">Alle</option>
                {['HS', 'HS PV', 'HS PV duo', 'HS GenLink', 'GS', 'LS'].map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Zugewiesener Mitarbeiter</Label>
            <select value={assignee} onChange={e => setAssignee(e.target.value)}
                    className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid="export-assignee">
              <option value="">Alle</option>
              {users.map(u => <option key={u.email} value={u.email}>{u.name} ({u.email})</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <Button variant="outline" onClick={onClose} className="h-9 text-sm">Abbrechen</Button>
          <Button onClick={download} disabled={downloading}
                  className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm" data-testid="btn-export-download">
            <Download className="w-3.5 h-3.5 mr-1" /> {downloading ? 'Lade…' : 'XLSX herunterladen'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Support Settings dialog (hourly rate) ─────────────────────────────
function SupportSettingsDialog({ hourlyRate, setHourlyRate, onClose }) {
  const [value, setValue] = useState(hourlyRate);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    const res = await authFetch('/api/tickets/_/settings', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hourlyRate: Number(value) }),
    });
    if (res.ok) { setHourlyRate(Number(value)); onClose(); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-semibold text-[#32373c]">Support-Einstellungen</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <Label className="text-xs text-gray-500">Stundensatz (€/h)</Label>
        <Input type="number" step="0.01" value={value} onChange={e => setValue(e.target.value)}
               className="mt-1 h-9 text-sm" data-testid="hourly-rate" />
        <p className="text-[11px] text-gray-400 mt-1">Dieser Satz wird für Personalkosten im Ticket-Report verwendet.</p>
        <div className="flex gap-2 mt-4 justify-end">
          <Button variant="outline" onClick={onClose} className="h-9 text-sm">Abbrechen</Button>
          <Button onClick={save} disabled={saving}
                  className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm" data-testid="btn-save-hourly-rate">
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}
